import React from 'react';
import { FormSubmission, InputChange, RouteProps } from '../types';
import SplashPageContainer from '../components/SplashPageContainer';
import SplashPageForm from '../components/SplashPageForm';
import { Theme } from '../theme';
import { isValidEmail } from '../utils';
import { useImmer } from 'use-immer';
import SubmitButton from '../components/SubmitButton';
import AuthManager from '../AuthManager';
import IconManager, { IconType } from '../components/IconManager';
import { Link } from '@reach/router';
import { NonAuthRoutes } from '../routes';
import FunctionsManager from '../functions/FunctionsManager';

enum StateKeys {
    email = 'email',
}

interface State {
    email: string;
    emailValid?: boolean;
    submitting: boolean;
    submissionError: unknown;
    formSubmitted: boolean;
}

const initialResetPasswordState: State = {
    email: '',
    submitting: false,
    submissionError: null,
    formSubmitted: false,
};

export default function ResetPassword(_: RouteProps) {
    const [state, updateState] = useImmer(initialResetPasswordState);

    const handleInput = (e: InputChange) => {
        e.persist();
        updateState(draft => {
            draft.email = e.target.value;
            if (e.target.value.length >= 15) {
                draft.emailValid = isValidEmail(e.target.value);
            }
        });
    };

    const handleSubmit = async (e: FormSubmission): Promise<void> => {
        e.preventDefault();
        updateState(draft => void (draft.submitting = true));
        try {
            await FunctionsManager.user.sendResetPasswordEmail({ email: state.email });
        } catch (error) {
            console.log(error);
            updateState(draft => void (draft.submissionError = error));
        }
        updateState(draft => {
            draft.submitting = false;
            draft.formSubmitted = true;
        });
    };
    return (
        <SplashPageContainer>
            <SplashPageForm onSubmit={handleSubmit} className="mt-32">
                {state.formSubmitted ? (
                    <div className="w-full px-4 py-2 flex flex-col mx-auto justify-center">
                        <h3 className="text-lg text-center block mb-3">
                            An email has been sent to the provided address with a link to reset your password
                        </h3>
                        <IconManager
                            type={IconType.CHECK_CIRCLE}
                            size={125}
                            stroke="#34AC6F"
                            className="mx-auto"
                        />
                        <Link
                            to={NonAuthRoutes.LOGIN}
                            className="text-blue-600 hover:text-blue-700 mx-auto mt-2"
                        >
                            Return to login
                        </Link>
                    </div>
                ) : (
                    <>
                        <h3 className={`text-${Theme.darkBlue} text-xl mb-4 text-center font-semibold`}>
                            Reset your password
                        </h3>
                        <div className="mb-1 text-center">
                            <label
                                htmlFor={StateKeys.email}
                                className="block text-gray-700 text-sm font-bold mb-2"
                            >
                                Please enter the email associated with your account
                            </label>
                            <input
                                className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline ${
                                    state.emailValid === false ? 'border-red-500' : 'input-border'
                                }`}
                                type="email"
                                name={StateKeys.email}
                                value={state.email}
                                onChange={handleInput}
                            />
                        </div>
                        <span
                            className={`text-xs text-center block text-${
                                state.emailValid === false ? 'red' : 'gray'
                            }-600 mb-2`}
                        >
                            {state.emailValid === false
                                ? 'Please enter a valid email address'
                                : "We'll send you an email with instructions for setting a new password"}
                        </span>
                        <SubmitButton
                            loading={state.submitting}
                            disabled={!state.email || !state.emailValid}
                            className="w-full mt-3"
                        >
                            Request Password Reset
                        </SubmitButton>
                        <div className="flex flex-row justify-center items-center mt-2">
                            <Link
                                to={NonAuthRoutes.LOGIN}
                                className={`text-${Theme.darkBlue} hover-${Theme.darkBlueHover}`}
                            >
                                Return to login
                            </Link>
                        </div>
                    </>
                )}
            </SplashPageForm>
        </SplashPageContainer>
    );
}
